import { useEffect, useRef, useState } from 'react'
import { FormikError } from '@nv/basic-components'
import { nxConfig } from '@nv/utils'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import styles from './SearchAddress.module.scss'

export const SearchAddress = ({ formik, objFieldName }) => {
  const { t } = useTranslation()
  const [apiLoaded, setApiLoaded] = useState(!!window.google)
  const autocompleteRef = useRef(null)
  const inputRef = useRef(null)

  useEffect(() => {
    const apiKey = nxConfig.GOOGLE_API_KEY

    if (!apiKey) {
      console.error('Google API key is missing!')
      return
    }

    const loadGoogleMapsApi = () => {
      return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script')
        script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
        script.async = true
        script.defer = true
        script.onload = () => resolve()
        script.onerror = (err) => reject(err)
        document.head.appendChild(script)
      })
    }

    if (!apiLoaded)
      loadGoogleMapsApi()
        .then(() => setApiLoaded(true))
        .catch((err) => console.error('Error loading Google Maps API:', err))
  }, [])

  useEffect(() => {
    if (!apiLoaded || !inputRef.current) return

    let options = {
      fields: ['address_components', 'geometry', 'name', 'formatted_address'],
    }
    if (nxConfig.eligibleCountries) {
      //@ts-ignore
      options = { ...options, componentRestrictions: { country: nxConfig.eligibleCountries.split(',') } }
    }

    autocompleteRef.current = new window.google.maps.places.Autocomplete(inputRef.current, options)
    autocompleteRef.current.addListener('place_changed', () => {
      const place = autocompleteRef.current.getPlace()
      fillInAddress(place)
    })

    return () => {
      if (autocompleteRef.current) {
        window.google.maps.event.clearInstanceListeners(autocompleteRef.current)
      }
    }
  }, [apiLoaded])

  const fillInAddress = (place) => {
    formik.setFieldValue(`${objFieldName}.formattedAddress`, place.formatted_address)

    let address1 = ''
    let postcode = ''
    // Get each component of the address from the place details.
    // place.address_components are google.maps.GeocoderAddressComponent objects
    // which are documented at http://goo.gle/3l5i5Mr
    for (const component of place.address_components || []) {
      const componentType = component.types[0]

      switch (componentType) {
        case 'street_number':
          address1 = `${component.long_name} ${address1}`
          break
        case 'route':
          address1 += component.short_name
          break
        case 'postal_code':
          postcode = `${component.long_name}${postcode}`
          break
        case 'postal_code_suffix':
          postcode = `${postcode}-${component.long_name}`
          break
        case 'locality':
          formik.setFieldValue(`${objFieldName}.city`, component.long_name)
          break
        case 'administrative_area_level_1':
          formik.setFieldValue(`${objFieldName}.province`, component.short_name)
          break
        case 'country':
          formik.setFieldValue(`${objFieldName}.country`, component.long_name)
          break
        default:
          break
      }
    }
    if (postcode) formik.setFieldValue(`${objFieldName}.postalCode`, postcode)
    if (address1) formik.setFieldValue(`${objFieldName}.streetAddress`, address1)

    // After filling the form with address components
    // add information such as apartment, unit, or floor number.
  }

  const handleInputChange = (e) => {
    const value = e.target.value
    if (value === '') {
      formik.setFieldValue(`${objFieldName}.streetAddress`, '')
      formik.setFieldValue(`${objFieldName}.city`, '')
      formik.setFieldValue(`${objFieldName}.province`, '')
      formik.setFieldValue(`${objFieldName}.postalCode`, '')
      formik.setFieldValue(`${objFieldName}.country`, '')
    }
    formik.setFieldValue(`${objFieldName}.formattedAddress`, value)
  }

  if (!apiLoaded) {
    return <div>Loading address list...</div>
  }
  return (
    <div className={clsx(styles.container)}>
      <div className={clsx(styles.label, styles.required)}>{t('registration.searchAddress')}</div>
      <input
        className={styles.input}
        ref={inputRef}
        onChange={handleInputChange}
        name={`${objFieldName}.formattedAddress`}
        placeholder={t('registration.searchAddress')}
        value={formik.values[objFieldName].formattedAddress}
      />
      <FormikError formik={formik} fieldName={`${objFieldName}.streetAddress`} />
    </div>
  )
}
